import { NotificationFormValues, stopSendingOptions } from "./types";

/** Two decimal hours can round-trip to whole minutes without drift. */
export const CUSTOM_HOURS_DECIMAL_PLACES = 2;

export const minutesFromCustomHours = (hours: number): number =>
  Math.round(hours * 60);

export const customHoursFromMinutes = (minutes: number): number => {
  const factor = 10 ** CUSTOM_HOURS_DECIMAL_PLACES;
  return Math.round((minutes / 60) * factor) / factor;
};

/** Preview copy for a stop-sending window. Fractional hours always use hours + minutes. */
export const formatStopSendingDuration = (diffMinutes: number): string => {
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes !== 1 ? "s" : ""}`;
  }

  const diffHours = diffMinutes / 60;
  if (Number.isInteger(diffHours)) {
    return `${diffHours} hour${diffHours !== 1 ? "s" : ""}`;
  }

  const wholeHours = Math.floor(diffHours);
  const remainingMinutes = diffMinutes % 60;
  return `${wholeHours} hour${
    wholeHours !== 1 ? "s" : ""
  } ${remainingMinutes} minute${remainingMinutes !== 1 ? "s" : ""}`;
};

export const getMaxSendDateTime = (
  baseDate: Date,
  values: NotificationFormValues
): string | null => {
  if (values.stopSendingOption === "anytime") {
    return null;
  }

  if (!values.sendDateTime) {
    return null;
  }

  let minutesToAdd = 0;

  if (values.stopSendingTimeUnit === "custom") {
    // Custom time setting
    if (values.stopSendingCustomUnit === "minutes") {
      minutesToAdd = values.stopSendingCustomValue;
    } else {
      minutesToAdd = minutesFromCustomHours(values.stopSendingCustomValue);
    }
  } else {
    // Predefined option
    const selectedOption = stopSendingOptions.find((option) => {
      if (values.stopSendingTimeValue === 5) return option.value === "5min";
      if (values.stopSendingTimeValue === 15) return option.value === "15min";
      if (values.stopSendingTimeValue === 30) return option.value === "30min";
      if (
        values.stopSendingTimeValue === 1 &&
        values.stopSendingTimeUnit === "hours"
      )
        return option.value === "1hour";
      if (
        values.stopSendingTimeValue === 3 &&
        values.stopSendingTimeUnit === "hours"
      )
        return option.value === "3hours";
      if (
        values.stopSendingTimeValue === 6 &&
        values.stopSendingTimeUnit === "hours"
      )
        return option.value === "6hours";
      if (
        values.stopSendingTimeValue === 12 &&
        values.stopSendingTimeUnit === "hours"
      )
        return option.value === "12hours";
      return false;
    });

    if (selectedOption) {
      if (selectedOption.minutes) {
        minutesToAdd = selectedOption.minutes;
      } else if (selectedOption.hours) {
        minutesToAdd = selectedOption.hours * 60;
      }
    }
  }

  const newDate = new Date(baseDate.getTime() + minutesToAdd * 60000);
  return newDate.toISOString().replace(/\.[0-9]{3}Z$/, ".0000000Z");
};
